import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog } from '@/components/ui/Dialog';
import { SignatureCapture } from '@/components/SignatureCapture';
import { PhotoCapture } from '@/components/PhotoCapture';
import { BillPhotos } from '@/components/BillPhotos';
import { AvsToggles } from '@/components/AvsToggles';
import { ShipmentInfoCard } from '@/components/ShipmentInfoCard';
import { NavigateButton } from '@/components/NavigateButton';
import { RouteMapPreview } from '@/components/RouteMapPreview';
import { GpsStatusLine } from '@/components/GpsStatusLine';
import { usePickupStore, pickupSelectors } from '@/stores/pickupStore';
import { useWorkflowBack } from '@/lib/useWorkflowBack';
import { TemperatureConfig } from '@/lib/config';

export function PickupProgressPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stop = usePickupStore((s) => s.stop);
  const phase = usePickupStore((s) => s.phase);
  const loadStop = usePickupStore((s) => s.loadStop);
  const reset = usePickupStore((s) => s.reset);
  const stepBack = usePickupStore((s) => s.stepBack);

  useEffect(() => {
    if (id && !stop) void loadStop(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Idle on the progress URL (e.g. after cancel) — return to the detail screen.
  useEffect(() => {
    if (phase === 'idle' && stop && id) {
      navigate(`/load/${id}`, { replace: true });
    }
  }, [phase, stop, id, navigate]);

  useWorkflowBack(phase !== 'idle' && phase !== 'complete', async () => {
    await stepBack();
    if (usePickupStore.getState().phase === 'idle' && id) {
      navigate(`/load/${id}`, { replace: true });
    }
  });

  // Native auto-returns to the main menu ~2s after completion.
  useEffect(() => {
    if (phase !== 'complete') return;
    const t = setTimeout(() => {
      reset();
      navigate('/main', { replace: true });
    }, 2000);
    return () => clearTimeout(t);
  }, [phase, navigate, reset]);

  if (!stop) {
    return (
      <AppShell title="Pickup" showBack>
        <p className="text-brand-muted">Loading...</p>
      </AppShell>
    );
  }

  return (
    <AppShell title={`Pickup: ${stop.name || stop.address}`}>
      {phase === 'enRoute' && <EnRoutePhase />}
      {phase === 'arrived' && <ArrivedPhase />}
      {phase === 'temperatureCheck' && <TemperaturePhase />}
      {phase === 'workBills' && <WorkBillsPhase />}
      {phase === 'signatureGate' && <SignaturePhase />}
      {phase === 'complete' && (
        <Card>
          <CardBody>
            <p className="font-semibold text-brand-delivery">Pickup complete.</p>
            <p className="text-sm text-brand-muted">Returning to the main menu...</p>
            <Button
              variant="primary"
              onClick={() => {
                reset();
                navigate('/main', { replace: true });
              }}
            >
              Back to main menu
            </Button>
          </CardBody>
        </Card>
      )}
    </AppShell>
  );
}

function EnRoutePhase() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stop = usePickupStore((s) => s.stop);
  const arrive = usePickupStore((s) => s.arrive);
  const cancelEnRoute = usePickupStore((s) => s.cancelEnRoute);
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3">
      {stop && <ShipmentInfoCard stop={stop} />}
      <Card>
      <CardHeader>
        <CardTitle>En route to pickup</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <GpsStatusLine />
        {stop && (
          <RouteMapPreview
            destination={{ name: stop.name, address: stop.address, city: stop.city }}
          />
        )}
        {stop && (
          <NavigateButton
            accent="pickup"
            destination={{ name: stop.name, address: stop.address, city: stop.city }}
          />
        )}
        <Button
          variant="pickup"
          size="xl"
          fullWidth
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await arrive();
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? 'Recording...' : 'ARRIVED'}
        </Button>
        <Button
          variant="danger"
          size="lg"
          fullWidth
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await cancelEnRoute();
            } finally {
              // Always leave the screen, even if the flow state was lost
              // (e.g. after a mid-route reload) and the cancel had nothing
              // to log. A button that silently does nothing reads as frozen.
              setBusy(false);
              navigate(`/load/${id}`, { replace: true });
            }
          }}
        >
          CANCEL
        </Button>
      </CardBody>
    </Card>
    </div>
  );
}

function ArrivedPhase() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const stop = usePickupStore((s) => s.stop);
  const beginLoading = usePickupStore((s) => s.beginLoading);
  const [busy, setBusy] = useState(false);
  return (
    <div className="space-y-3">
      {stop && <ShipmentInfoCard stop={stop} />}
      <Card>
      <CardHeader>
        <CardTitle>Arrived at pickup</CardTitle>
      </CardHeader>
      <CardBody>
        <Button
          variant="pickup"
          size="xl"
          fullWidth
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await beginLoading();
            } finally {
              setBusy(false);
            }
          }}
        >
          START LOADING
        </Button>
        <Button variant="danger" size="lg" fullWidth onClick={() => navigate(`/load/${id}/dead-call`)}>
          DEAD CALL
        </Button>
      </CardBody>
    </Card>
    </div>
  );
}

function TemperaturePhase() {
  const enter = usePickupStore((s) => s.enterTemperature);
  const confirm = usePickupStore((s) => s.confirmTemperature);
  const refuse = usePickupStore((s) => s.refuseTemperature);
  const exceeds = usePickupStore(pickupSelectors.temperatureExceedsThreshold);
  const value = usePickupStore((s) => s.enteredTemperature);
  const [text, setText] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);

  const onChange = (raw: string) => {
    setText(raw);
    const n = parseFloat(raw);
    if (Number.isFinite(n)) enter(n);
  };

  return (
    <Card accent="pickup">
      <CardHeader>
        <CardTitle>Temperature probe</CardTitle>
      </CardHeader>
      <CardBody>
        <p className="text-sm text-brand-muted">
          Capture the load temperature. Threshold: {TemperatureConfig.thresholdCelsius} C.
        </p>
        <Input
          label="Temperature (C)"
          type="number"
          step="0.1"
          inputMode="decimal"
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
        {value !== null && (
          <p className={exceeds ? 'font-bold text-brand-error' : 'font-bold text-brand-delivery'}>
            {exceeds
              ? `EXCEEDS ${TemperatureConfig.thresholdCelsius} C - refuse pickup`
              : 'OK - within range'}
          </p>
        )}
        {value === null ? null : exceeds ? (
          <div className="grid grid-cols-2 gap-3">
            <Button variant="danger" size="lg" onClick={() => void refuse()}>
              Refuse pickup
            </Button>
            <Button variant="outline" size="lg" onClick={() => setConfirmDialog(true)}>
              Override
            </Button>
          </div>
        ) : (
          <Button variant="delivery" size="lg" fullWidth onClick={() => void confirm(false)}>
            Continue to loading
          </Button>
        )}
        <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} title="Override temperature?">
          <p className="text-sm">
            Accepting a load at {value}C exceeds the {TemperatureConfig.thresholdCelsius}C threshold.
            This decision is logged.
          </p>
          <div className="mt-3 flex gap-2">
            <Button variant="ghost" fullWidth onClick={() => setConfirmDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              fullWidth
              onClick={() => {
                setConfirmDialog(false);
                void confirm(true);
              }}
            >
              Confirm override
            </Button>
          </div>
        </Dialog>
      </CardBody>
    </Card>
  );
}

function WorkBillsPhase() {
  const stop = usePickupStore((s) => s.stop);
  const bills = usePickupStore((s) => s.bills);
  const avs = usePickupStore((s) => s.avs);
  const setAvs = usePickupStore((s) => s.setAvs);
  const setNoFreight = usePickupStore((s) => s.setBillNoFreight);
  const setConfirmed = usePickupStore((s) => s.setBillConfirmed);
  const setNotes = usePickupStore((s) => s.setBillNotes);
  const addPhoto = usePickupStore((s) => s.addBillPhoto);
  const removePhoto = usePickupStore((s) => s.removeBillPhoto);
  const setBolDoc = usePickupStore((s) => s.setBillBolDoc);
  const cancelLoading = usePickupStore((s) => s.cancelLoading);
  const canContinue = usePickupStore(pickupSelectors.canContinueToSignature);

  // Native auto-advances to the signature gate the moment every bill is handled
  // (confirmed or No Freight) with at least one real pickup -- there is no
  // explicit "continue" button in the work-bills step.
  useEffect(() => {
    if (canContinue) usePickupStore.setState({ phase: 'signatureGate' });
  }, [canContinue]);

  if (!stop) return null;

  return (
    <div className="space-y-3">
      <Card>
        <CardBody>
          <AvsToggles
            values={avs}
            onChange={(patch) => setAvs(patch)}
            fields={['inside', 'tailgate', 'residential', 'dangerousGoods']}
          />
        </CardBody>
      </Card>
      {stop.bills.map((b) => {
        const bs = bills[b.remF04Id];
        return (
          <Card key={b.remF04Id}>
            <CardHeader>
              <CardTitle>{b.remF02Tag ?? b.remF04Id}</CardTitle>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-5 w-5"
                  checked={bs?.noFreight ?? false}
                  onChange={(e) => setNoFreight(b.remF04Id, e.target.checked)}
                />
                No Freight
              </label>
            </CardHeader>
            {(b.remConsigneeName || b.remConsigneeAddress) && (
              <p className="-mt-2 mb-3 text-base text-brand-muted">
                Deliver to:{' '}
                <span className="font-semibold text-brand-text">{b.remConsigneeName}</span>
                {b.remConsigneeAddress ? <> · {b.remConsigneeAddress}</> : null}
                {b.remConsigneeCity ? <>, {b.remConsigneeCity}</> : null}
              </p>
            )}
            {!bs?.noFreight && (
              <CardBody>
                <Button
                  variant={bs?.confirmed ? 'outline' : 'pickup'}
                  size="lg"
                  fullWidth
                  onClick={() => setConfirmed(b.remF04Id, !bs?.confirmed)}
                >
                  {bs?.confirmed ? 'Bill loaded ✓' : 'Confirm bill loaded'}
                </Button>
                <Input
                  label="Notes"
                  value={bs?.notes ?? ''}
                  onChange={(e) => setNotes(b.remF04Id, e.target.value)}
                />
                <BillPhotos
                  photos={bs?.photos ?? []}
                  onAdd={(blob) => addPhoto(b.remF04Id, blob)}
                  onRemove={(i) => removePhoto(b.remF04Id, i)}
                />
                <div className="mt-2">
                  <label className="block text-sm font-semibold text-brand-muted">
                    BOL document {bs?.bolDoc ? '(captured)' : '(optional)'}
                  </label>
                  <PhotoCapture
                    label="Capture BOL"
                    value={bs?.bolDoc ?? null}
                    onCapture={(blob) => setBolDoc(b.remF04Id, blob)}
                    onRemove={() => setBolDoc(b.remF04Id, null)}
                  />
                </div>
              </CardBody>
            )}
          </Card>
        );
      })}
      <Button variant="danger" size="lg" fullWidth onClick={() => cancelLoading()}>
        CANCEL LOADING
      </Button>
    </div>
  );
}

function SignaturePhase() {
  const captureSignature = usePickupStore((s) => s.captureSignature);
  const setSignerName = usePickupStore((s) => s.setSignerName);
  const signerName = usePickupStore((s) => s.signerName);
  const setRoomLeft = usePickupStore((s) => s.setRoomLeft);
  const roomLeft = usePickupStore((s) => s.roomLeft);
  const complete = usePickupStore((s) => s.complete);
  const [signed, setSigned] = useState(false);
  const [signing, setSigning] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (signing) {
    return (
      <SignatureCapture
        accent="pickup"
        onCancel={() => setSigning(false)}
        onAccept={async (blob) => {
          captureSignature(blob);
          setSigned(true);
          setSigning(false);
        }}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Complete pickup</CardTitle>
      </CardHeader>
      <CardBody>
        <Button
          variant="outline"
          size="lg"
          fullWidth
          onClick={() => setSigning(true)}
        >
          {signed ? 'Signature Captured \u2713' : 'Tap to Sign (optional)'}
        </Button>
        <p className="text-xs text-brand-muted">
          The shipper's BOL is the legal shipping document, so a signature is optional.
        </p>
        <Input label="Signer name" value={signerName} onChange={(e) => setSignerName(e.target.value)} />
        <Input
          label="Room left (required)"
          type="text"
          inputMode="numeric"
          value={roomLeft ?? ''}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '');
            setRoomLeft(digits === '' ? null : Number(digits));
          }}
        />
        <Button
          variant="delivery"
          size="xl"
          fullWidth
          disabled={submitting || roomLeft === null || (signed && !signerName.trim())}
          onClick={async () => {
            setSubmitting(true);
            try {
              await complete();
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {submitting ? 'Submitting...' : 'COMPLETE PICKUP'}
        </Button>
      </CardBody>
    </Card>
  );
}
